import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Fade,
  IconButton,
  Modal,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import PaidIcon from '@mui/icons-material/Paid';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import _ from 'lodash';

import api from '../../utils/functions/api';
import { showAlert } from '../../utils/functions/globalDialog';
import { fmtMoney } from '../../components/Modal/PendingItemsModal';
import { REFUND_PENDING_COLOR, REFUND_RECEIVED_COLOR } from '../../components/Modal/RefundItemsModal';
import ExpensesDetail from './ExpensesDetail';

const STATUS_FILTERS = [
  { key: 'PENDING', label: 'รอคืน', color: REFUND_PENDING_COLOR },
  { key: 'SUCCESS', label: 'คืนแล้ว', color: REFUND_RECEIVED_COLOR },
  { key: '', label: 'ทั้งหมด', color: '#64748b' },
];

// แท็บ "รอรับคืน" — รายการเบิกรอรับคืน (Expenses.isRefund) ที่สร้างคู่กับ Budget.refund_items จาก
// RefundItemsModal มีแค่ 2 สถานะ PENDING/SUCCESS กด "ได้รับคืน" ได้เลยไม่ต้องผ่านขั้นอนุมัติ — ซิงค์กลับไป
// ที่รายการรอคืนของโครงการ (backend: budget.service.js receiveRefundByExpense)
export default function ExpensesTabRefund({ refreshTick, onDoubleClickProject }) {
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [receivingId, setReceivingId] = useState(null);
  // ดับเบิลคลิกแถว = เปิดรายละเอียดรายการเบิก (เห็นสลิปใน Bill Images) — modal เดียวกับ ExpensesTableApplove.js
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `${process.env.REACT_APP_API_URL}/expenses/refunds${statusFilter ? `?status=${statusFilter}` : ''}`,
      );
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load, refreshTick]);

  // dialog ยืนยันได้รับคืน — บังคับแนบสลิปเหมือนใน RefundItemsModal (backend ก็ปฏิเสธถ้าไม่มี slip_url)
  const [receiveRow, setReceiveRow] = useState(null);
  const [receiveFile, setReceiveFile] = useState(null);
  const [receivePreviewUrl, setReceivePreviewUrl] = useState(null);
  useEffect(() => {
    if (!receiveFile || !receiveFile.type?.startsWith('image/')) {
      setReceivePreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(receiveFile);
    setReceivePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [receiveFile]);

  const openReceive = (row) => {
    setReceiveRow(row);
    setReceiveFile(null);
  };
  const closeReceive = () => {
    if (receivingId) return;
    setReceiveRow(null);
    setReceiveFile(null);
  };

  const handleReceive = async () => {
    if (!receiveRow || !receiveFile) return;
    setReceivingId(receiveRow._id);
    try {
      const formData = new FormData();
      formData.append('file', receiveFile, receiveFile.name);
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/expenses/upload-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (!data?.url) throw new Error('upload failed');
      await api.patch(
        `${process.env.REACT_APP_API_URL}/expenses/refunds/${receiveRow._id}/receive`,
        { slip_url: data.url, slip_image_id: data.id },
      );
      setReceiveRow(null);
      setReceiveFile(null);
      await load();
    } catch (error) {
      showAlert('บันทึกการรับเงินคืนไม่สำเร็จ');
    } finally {
      setReceivingId(null);
    }
  };

  const total = _.sumBy(rows, (r) => Number(r?.price) || 0);

  return (
    <Box>
      {/* Filter + summary */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'inline-flex', p: 0.5, gap: 0.5, borderRadius: 999, bgcolor: 'action.hover' }}>
          {STATUS_FILTERS.map((f) => {
            const active = statusFilter === f.key;
            return (
              <Box
                key={f.key || 'all'}
                component="button"
                type="button"
                onClick={() => setStatusFilter(f.key)}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  px: 1.75,
                  py: 0.6,
                  borderRadius: 999,
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  color: active ? '#fff' : 'text.secondary',
                  bgcolor: active ? f.color : 'transparent',
                  transition: 'all .15s ease',
                  '&:hover': { color: active ? '#fff' : f.color },
                }}
              >
                {f.label}
              </Box>
            );
          })}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {rows.length} รายการ · รวม{' '}
          <Box component="span" sx={{ fontWeight: 800, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
            ฿{fmtMoney(total)}
          </Box>
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 700, bgcolor: 'action.hover', whiteSpace: 'nowrap' } }}>
              <TableCell>รหัส</TableCell>
              <TableCell>ชื่อรายการคืน</TableCell>
              <TableCell>ค่าคืนของรายการเบิก</TableCell>
              <TableCell>โครงการ / งบ</TableCell>
              <TableCell align="right">จำนวนเงิน</TableCell>
              <TableCell align="center">สถานะ</TableCell>
              <TableCell align="center">จัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  ไม่มีรายการ
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const received = row.status === 'SUCCESS';
                const color = received ? REFUND_RECEIVED_COLOR : REFUND_PENDING_COLOR;
                const projectId = row.project?._id || row.project;
                return (
                  <TableRow
                    key={row._id}
                    hover
                    onDoubleClick={() => setDetailId(row._id)}
                    sx={{ verticalAlign: 'top', cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {row.code || '-'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>{row.name}</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Stack spacing={0.5}>
                        {(row.refund_source_expenses || []).map((src) => (
                          <Box key={src._id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                            <ReceiptLongOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', mt: 0.25 }} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{src.name || '-'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {src.code || '-'}
                                {src.date ? ` · ${dayjs(src.date).format('DD/MM/YYYY')}` : ''}
                                {` · ฿${fmtMoney(src.price)}`}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell
                      sx={{ minWidth: 180, cursor: projectId ? 'pointer' : 'default' }}
                      onDoubleClick={(ev) => {
                        if (!projectId) return;
                        ev.stopPropagation();
                        onDoubleClickProject?.(projectId);
                      }}
                    >
                      <Tooltip title={projectId ? 'ดับเบิลคลิกเพื่อเปิดโครงการ' : ''}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {row.project?.project_number || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {row.project?.name || ''}
                          </Typography>
                          <Typography variant="caption" sx={{ color: REFUND_PENDING_COLOR, fontWeight: 600 }}>
                            {row.budget ? `${row.budget.prefix || ''}${row.budget.budget_number || ''} ${row.budget.name || ''}` : ''}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      ฿{fmtMoney(row.price)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={received ? 'คืนแล้ว' : 'รอคืน'}
                        sx={{ fontWeight: 700, color, bgcolor: alpha(color, 0.12) }}
                      />
                      {received && row.refunded_at && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {dayjs(row.refunded_at).format('DD/MM/YYYY')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {!received && (
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          startIcon={receivingId === row._id ? <CircularProgress size={14} color="inherit" /> : <PaidIcon fontSize="small" />}
                          disabled={receivingId === row._id}
                          onClick={() => openReceive(row)}
                          sx={{ whiteSpace: 'nowrap', borderRadius: 999, bgcolor: REFUND_RECEIVED_COLOR, '&:hover': { bgcolor: '#1b5e20' } }}
                        >
                          ได้รับคืน
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Modal open={!!detailId} onClose={() => setDetailId(null)} closeAfterTransition>
        <Fade in={!!detailId}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '98%', sm: '95%', md: '90%', lg: '82%' },
              maxHeight: '95vh',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'primary.main',
                color: 'white',
                flexShrink: 0,
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">รายละเอียดรายการเบิกจ่าย</Typography>
              <IconButton size="small" onClick={() => setDetailId(null)} sx={{ color: 'white' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ overflowY: 'auto', flex: 1, p: { xs: 1, sm: 2 } }}>
              {detailId && <ExpensesDetail propId={detailId} onClose={() => setDetailId(null)} />}
            </Box>
          </Box>
        </Fade>
      </Modal>

      <Dialog
        open={!!receiveRow}
        onClose={closeReceive}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: `linear-gradient(135deg, ${alpha(REFUND_RECEIVED_COLOR, 0.16)} 0%, ${alpha(REFUND_RECEIVED_COLOR, 0.03)} 100%)`,
            borderBottom: '1px solid',
            borderColor: alpha(REFUND_RECEIVED_COLOR, 0.15),
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              bgcolor: REFUND_RECEIVED_COLOR,
              boxShadow: `0 4px 12px ${alpha(REFUND_RECEIVED_COLOR, 0.35)}`,
              flexShrink: 0,
            }}
          >
            <PaidIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>ยืนยันได้รับเงินคืน</Typography>
            <Typography variant="body2" noWrap sx={{ color: 'text.secondary' }}>
              {receiveRow?.project?.project_number || ''} {receiveRow?.code ? `· ${receiveRow.code}` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeReceive} disabled={!!receivingId} sx={{ alignSelf: 'flex-start' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2.5,
              textAlign: 'center',
              border: '1px solid',
              borderColor: alpha(REFUND_RECEIVED_COLOR, 0.25),
              bgcolor: alpha(REFUND_RECEIVED_COLOR, 0.05),
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>ยอดเงินที่ได้รับคืน</Typography>
            <Typography sx={{ fontSize: '1.9rem', fontWeight: 800, lineHeight: 1.2, color: REFUND_RECEIVED_COLOR, fontVariantNumeric: 'tabular-nums' }}>
              ฿{fmtMoney(receiveRow?.price)}
            </Typography>
            <Typography variant="body2" fontWeight={700} sx={{ mt: 0.75, wordBreak: 'break-word' }}>{receiveRow?.name}</Typography>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, mb: 0.5 }}>
            หลักฐานการรับเงินคืน <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </Typography>
          {receiveFile ? (
            <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              {receivePreviewUrl && (
                <Box
                  component="img"
                  src={receivePreviewUrl}
                  alt=""
                  sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1.5, flexShrink: 0, bgcolor: 'action.hover' }}
                />
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap title={receiveFile.name}>{receiveFile.name}</Typography>
                <Typography variant="caption" color="text.secondary">{(receiveFile.size / 1024).toFixed(0)} KB</Typography>
              </Box>
              <Button component="label" size="small" disabled={!!receivingId} sx={{ whiteSpace: 'nowrap' }}>
                เปลี่ยน
                <input type="file" accept="image/*" hidden onChange={(ev) => setReceiveFile(ev.target.files?.[0] || null)} />
              </Button>
              <Tooltip title="เอาออก">
                <span>
                  <IconButton size="small" onClick={() => setReceiveFile(null)} disabled={!!receivingId}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ) : (
            <Box
              component="label"
              sx={{
                py: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                borderRadius: 2,
                border: '1px dashed',
                borderColor: alpha(REFUND_RECEIVED_COLOR, 0.45),
                color: 'text.secondary',
                cursor: 'pointer',
                '&:hover': { bgcolor: alpha(REFUND_RECEIVED_COLOR, 0.05), borderColor: REFUND_RECEIVED_COLOR },
              }}
            >
              <CloudUploadOutlinedIcon sx={{ color: REFUND_RECEIVED_COLOR }} />
              <Typography variant="body2" fontWeight={600} sx={{ color: REFUND_RECEIVED_COLOR }}>คลิกเพื่อแนบรูปสลิป/หลักฐาน</Typography>
              <Typography variant="caption">ต้องแนบก่อนยืนยัน · รองรับไฟล์รูปภาพ</Typography>
              <input type="file" accept="image/*" hidden onChange={(ev) => setReceiveFile(ev.target.files?.[0] || null)} />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeReceive} disabled={!!receivingId} sx={{ color: 'text.secondary' }}>ยกเลิก</Button>
          <Button
            variant="contained"
            disableElevation
            startIcon={receivingId ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            sx={{ bgcolor: REFUND_RECEIVED_COLOR, borderRadius: 999, px: 2.5, '&:hover': { bgcolor: '#1b5e20' } }}
            onClick={handleReceive}
            disabled={!!receivingId || !receiveFile}
          >
            {receivingId ? 'กำลังบันทึก...' : 'ยืนยันได้รับเงินคืน'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
